import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useTranslation } from 'react-i18next';
import CookieConsent from '../../components/feature/CookieConsent';
import ExtensionSelectionModal from '../../components/feature/ExtensionSelectionModal';
import ConsumerExtensionModal from '../../components/feature/ConsumerExtensionModal';
import VideoModal from '../../components/feature/VideoModal';
import SEO from '../../components/feature/SEO';
import InvoiceGalleryMarquee from '@/pages/custom-invoice-design/components/InvoiceGalleryMarquee';

export default function HomePage() {
  const { t } = useTranslation('home');
  const [isVisible, setIsVisible] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [problemCardsVisible, setProblemCardsVisible] = useState(false);
  const [activeLightbox, setActiveLightbox] = useState<string | null>(null);
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [isConsumerModalOpen, setIsConsumerModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const problemSectionRef = useRef<HTMLDivElement>(null);

  const textVariations: { line1?: string; line1Pre?: string; line1BlueWord?: string; line1Post?: string; line2: string; line3: string; line3Blue: string }[] = [
    { line1: 'Secure Your', line2: 'Xero', line3: 'Invoices with', line3Blue: 'XeroVerifier' },
    { line1Pre: 'Custom ', line1BlueWord: 'Style', line1Post: ' Your', line2: 'Xero', line3: 'Invoices with', line3Blue: 'XeroVerifier' },
    { line1: 'Protect Your', line2: 'Business Reputation', line3: 'From Scam Invoices', line3Blue: '' },
    { line1: 'Protect Your', line2: 'Customers', line3: 'From Scam Invoices', line3Blue: '' },
    { line1: 'Protect Your', line2: 'Income', line3: 'From Scam Invoices', line3Blue: '' },
  ];

  const featureDetails = [
    {
      id: 'qr-code',
      title: 'QR Code Verification',
      content: 'Each invoice includes a unique bioQR code, combined with patented cryptography using military grade AES256 data encryption. Consumers can scan the code using our free proprietary browser extension, or our bioVerifier Mobile Phone app, and instantly verify authenticity of the Sender through our bioMail platform.'
    },
    {
      id: 'tamper-proof',
      title: 'Tamper-Proof Stamps',
      content: 'Advanced cryptographic signatures ensure that verification stamps cannot be copied or replicated by fraudsters. These signatures, used in a patented manner in conjunction with our back-end technology, provide solid protection from Invoice and Document Fraud.'
    },
    {
      id: 'real-time',
      title: 'Real-Time Verification',
      content: 'When the receiving party clicks on the XeroVerifier Stamp, using the proprietary and patented free browser extension, they will immediately see who sent the invoice, when, to who, and other relevant information. A warning will appear if the system detects the XeroVerifier Stamp has been tampered with.'
    },
    {
      id: 'fraud-alerts',
      title: 'Professionally Styled Invoices',
      content: 'Choose between the provided invoice template and add your own header graphic, or request a design specific to you.'
    },
    {
      id: 'analytics',
      title: 'Analytics Dashboard',
      content: 'Track verification attempts, monitor security threats, and gain insights into your invoice security performance. As a fully registered member using XeroVerifier paid versions, you have access to your Analytic Dashboard. This is not available to the limited free version.'
    },
    {
      id: 'support',
      title: 'Committed Support',
      content: 'Our dedicated team is available to help you design a stylish invoice you can be proud of, and assist you and your clients with any XeroVerifier verification questions.'
    }
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Scroll observer for problem cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setProblemCardsVisible(true);
          }
        });
      },
      { threshold: 0.7 }
    );

    if (problemSectionRef.current) {
      observer.observe(problemSectionRef.current);
    }

    return () => {
      if (problemSectionRef.current) {
        observer.unobserve(problemSectionRef.current);
      }
    };
  }, []);

  // Typing effect for line 2
  useEffect(() => {
    const currentLine2 = textVariations[currentTextIndex].line2;
    
    if (isTyping) {
      if (displayedText.length < currentLine2.length) {
        const timeout = setTimeout(() => {
          setDisplayedText(currentLine2.slice(0, displayedText.length + 1));
        }, 100);
        return () => clearTimeout(timeout);
      } else {
        // Wait before switching to next text — hold longer on index 1 (Custom Style slide)
        const holdDuration = currentTextIndex === 1 ? 5000 : 3000;
        const timeout = setTimeout(() => {
          setIsTyping(false);
          setDisplayedText('');
        }, holdDuration);
        return () => clearTimeout(timeout);
      }
    } else {
      // Move to next text variation
      const timeout = setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % textVariations.length);
        setIsTyping(true);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [displayedText, isTyping, currentTextIndex]);

  const currentText = textVariations[currentTextIndex];

  const openLightbox = (id: string) => {
    setActiveLightbox(id);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setActiveLightbox(null);
    document.body.style.overflow = 'unset';
  };

  const openExtensionModal = () => {
    setIsExtensionModalOpen(true);
  };

  const closeExtensionModal = () => {
    setIsExtensionModalOpen(false);
  };

  const openConsumerModal = () => {
    setIsConsumerModalOpen(true);
  };

  const closeConsumerModal = () => {
    setIsConsumerModalOpen(false);
  };

  const openVideoModal = () => {
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="XeroVerifier | Invoice Fraud | Styled Invoices"
        description="Protect your business reputation with XeroVerifier - the Xero extension that stamps invoices with verification codes to prevent fraud and ensure authenticity. Install now for free trial."
        keywords="Xero security, invoice verification, fraud protection, QR code verification, business security, invoice fraud prevention, Xero extension"
        canonical="/"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'XeroVerifier',
          description: 'Xero extension that stamps invoices with verification codes to prevent invoice fraud and ensure authenticity.',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Chrome Extension',
          url: `${import.meta.env.VITE_SITE_URL || ''}/`,
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            ratingCount: '1250',
            bestRating: '5',
            worstRating: '1',
          },
          publisher: {
            '@type': 'Organization',
            name: 'XeroVerifier',
            url: `${import.meta.env.VITE_SITE_URL || ''}/`,
          },
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/023e88077fff9d769802d50d8ceba95c.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-transparent"></div>

        {/* Invoice overlay — visible immediately on page load, slides out after the "Custom Style Your" slide (index 1) */}
        <div
          className="absolute hidden lg:block transition-all duration-700 pointer-events-none"
          style={{
            right: '310px',
            bottom: '-40px',
            width: '420px',
            transformOrigin: 'bottom left',
            transform: `rotate(26deg) translateX(${(currentTextIndex === 0 || currentTextIndex === 1) ? '0px' : '140px'})`,
            opacity: (currentTextIndex === 0 || currentTextIndex === 1) ? 1 : 0,
            zIndex: 10,
          }}
        >
          <img
            src="https://storage.readdy-site.link/project_files/76e476ea-c87d-45fb-a794-b173053f1134/6aecfff9-683d-40dc-8ce2-f549d87cbba2_Invoice_New1.jpg?v=aed02163ef45766f41799a24f63415a3"
            alt="Custom styled Xero invoice example"
            className="w-full rounded-xl"
            style={{ boxShadow: '0 30px 60px rgba(0,0,0,0.25)' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <p className="text-5xl lg:text-6xl font-bold leading-tight mb-2">
                <span style={{ color: '#29B3E6' }}>Style</span>
                <span className="text-gray-900"> Your Invoices</span>
              </p>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {currentText.line1BlueWord ? (
                  <>
                    {currentText.line1Pre}
                    <span style={{ color: '#29B3E6' }}>{currentText.line1BlueWord}</span>
                    {currentText.line1Post}
                  </>
                ) : currentText.line1}
                <span style={{ color: '#29B3E6' }} className="block min-h-[1.2em]">
                  {displayedText}
                  <span className="inline-block w-0.5 h-[0.9em] ml-1 animate-pulse" style={{ backgroundColor: '#29B3E6' }}></span>
                </span>
                <span className="whitespace-nowrap">
                  {currentText.line3}{' '}
                  {currentText.line3Blue && <span style={{ color: '#29B3E6' }}>{currentText.line3Blue}</span>}
                </span>
              </h1>
              <p className="text-xl text-gray-600 mt-6 leading-relaxed">
                Protect your business reputation and prevent fraud with XeroVerifier. <span style={{ color: '#29B3E6' }}>"Digitally Stamp"</span> every invoice with a unique cryptographic verification code that proves authenticity and builds client trust.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <button 
                  className="text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg whitespace-nowrap" 
                  style={{ backgroundColor: '#29B3E6' }} 
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1E9BD1'} 
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#29B3E6'}
                  onClick={openExtensionModal}
                >
                  <i className="ri-download-line mr-2"></i>
                  Install Extension
                </button>
                {/* Temporarily hidden Watch Demo button
                <button 
                  className="border-2 px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap" 
                  style={{ borderColor: '#29B3E6', color: '#29B3E6' }} 
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F0F9FF'} 
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  onClick={openVideoModal}
                >
                  <i className="ri-play-circle-line mr-2"></i>
                  Watch Demo
                </button>
                */}
              </div>
              <div className="flex items-center gap-6 mt-8">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-fill text-green-500 text-xl"></i>
                  <span className="text-sm text-gray-600">99.999% Secure</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-xl" style={{ color: '#29B3E6' }}></i>
                  <span className="text-sm text-gray-600">5-Minute Setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-star-fill text-yellow-500 text-xl"></i>
                  <span className="text-sm text-gray-600">5-Star Rated</span>
                </div>
              </div>
            </div>
            <div className="lg:block hidden"></div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20" style={{ backgroundColor: '#E0E7FF' }} ref={problemSectionRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Invoice Fraud is a Growing Threat
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cybercriminals are creating fake invoices and emails that damage your reputation and cost you and your clients money. 
Don't allow fraudsters to abuse your business name !
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className={`bg-white p-8 rounded-xl shadow-lg text-center transform transition-all duration-1500 ${problemCardsVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`} style={{ transitionDelay: '50ms' }}>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-error-warning-fill text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Lost Revenue</h3>
              <p className="text-gray-600">
                Clients pay fake invoices instead of legitimate ones, causing direct financial losses to your business, and your client.
              </p>
            </div>
            
            <div className={`bg-white p-8 rounded-xl shadow-lg text-center transform transition-all duration-1500 ${problemCardsVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`} style={{ transitionDelay: '250ms' }}>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-cross-fill text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Damaged Reputation</h3>
              <p className="text-gray-600">
                Fraudulent invoices using your business name destroys client trust and damages your professional reputation.
              </p>
            </div>
            
            <div className={`bg-white p-8 rounded-xl shadow-lg text-center transform transition-all duration-1500 ${problemCardsVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`} style={{ transitionDelay: '500ms' }}>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-unfollow-fill text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Client Loss</h3>
              <p className="text-gray-600">
                Confused and frustrated clients may stop doing business with you after falling victim to invoice fraud.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                The XeroVerifier Solution
              </h2>
              <p className="text-xl text-gray-600 mb-4">
                Our Chrome extension seamlessly integrates with Xero Online to give you Styled Invoices and add a unique verification stamp 
                to every invoice you send. Your clients can instantly verify authenticity and brand.
              </p>
              <p className="text-xl text-gray-600 mb-8">
                <span style={{ color: '#29B3E6' }}>Plus</span> you can get a beautifully styled invoice with your own header branding, or you can request a specific invoice layout styled just for you.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#E6F5FD' }}>
                    <i className="ri-check-line" style={{ color: '#29B3E6' }}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Automatic Verification Stamps</h3>
                    <p className="text-gray-600">Every invoice gets a unique QR code and verification number that proves it came from your Xero account.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#E6F5FD' }}>
                    <i className="ri-check-line" style={{ color: '#29B3E6' }}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Instant Invoice Verification</h3>
                    <p className="text-gray-600">Clients can scan the QR code or visit our verification portal to confirm invoice authenticity in seconds.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#E6F5FD' }}>
                    <i className="ri-check-line" style={{ color: '#29B3E6' }}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Fraud Prevention</h3>
                    <p className="text-gray-600">Impossible to replicate verification codes make it virtually impossible for fraudsters to create convincing fakes.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#E6F5FD' }}>
                    <i className="ri-check-line" style={{ color: '#29B3E6' }}></i>
                  </div>
                  <div className="relative flex-1">
                    <h3 className="font-semibold mb-2" style={{ color: '#0089D0' }}>Styled Invoices</h3>
                    <p className="text-gray-600 pr-10">Included is a professionally styled invoice with your own branded header image. 
Alternatively tell us what you have in mind and our design team will craft a bespoke layout just for your business.</p>
                    <Link 
                      to="/custom-invoice-design" 
                      className="absolute bottom-0 right-0 w-14 h-14 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                      style={{ backgroundColor: '#29B3E6' }}
                    >
                      <i className="ri-arrow-right-s-line text-white text-3xl"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div 
                className="rounded-xl shadow-2xl w-full overflow-y-auto"
                style={{ 
                  backgroundColor: '#F1F1EF',
                  height: '500px',
                  width: '100%'
                }}
              >
                <img 
                  src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/8b792743fab37a62962de513d3840603.jpeg"
                  alt="XeroVerifier in action"
                  className="object-center"
                  style={{ 
                    display: 'block', 
                    margin: '0 auto',
                    imageRendering: 'crisp-edges'
                  }}
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-green-500 text-white p-4 rounded-lg shadow-lg cursor-pointer" onClick={() => openLightbox('real-time')}>
                <i className="ri-shield-check-fill text-2xl mr-2"></i>
                <span className="font-semibold">Click to see it Verified Authentic</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Invoice Gallery Marquee */}
      <section className="py-16 overflow-hidden" style={{ backgroundColor: '#E0E7FF' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-10">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Styled Invoice Designs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Use our Free professionally styled invoice layout, or Design your own.
            </p>
          </div>
        </div>
        <InvoiceGalleryMarquee paddingX={150} />
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Powerful Features for Complete Protection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              XeroVerifier provides comprehensive invoice security with features designed for modern businesses.
            </p>
          </div>
          
          {/* First Row - 3 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow relative">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#E6F5FD' }}>
                <i className="ri-qr-code-line text-2xl" style={{ color: '#29B3E6' }}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">QR Code Verification</h3>
              <p className="text-gray-600 mb-12">
                Each invoice includes a unique bioQR code that clients can scan to instantly verify authenticity through our bioMail platform.
              </p>
              <button 
                onClick={() => openLightbox('qr-code')}
                className="absolute bottom-4 right-4 px-4 py-1.5 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: '#E0E7FF', color: '#7CB4FF' }}
              >
                Learn
              </button>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow relative">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Tamper-Proof Stamps</h3>
              <p className="text-gray-600 mb-12">
                Advanced cryptographic signatures ensure that verification stamps cannot be copied or replicated by fraudsters.
              </p>
              <button 
                onClick={() => openLightbox('tamper-proof')}
                className="absolute bottom-4 right-4 px-4 py-1.5 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: '#E0E7FF', color: '#7CB4FF' }}
              >
                Learn
              </button>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow relative">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-time-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Real-Time Verification</h3>
              <p className="text-gray-600 mb-12">
                Instant verification results help clients make confident payment decisions without delays or uncertainty.
              </p>
              <button 
                onClick={() => openLightbox('real-time')}
                className="absolute bottom-4 right-4 px-4 py-1.5 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: '#E0E7FF', color: '#7CB4FF' }}
              >
                Learn
              </button>
            </div>
          </div>

          {/* Second Row - 2 Centered Cards */}
          <div className="flex justify-center">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow relative">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-notification-3-line text-orange-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Professionally Styled Invoices</h3>
                <p className="text-gray-600 mb-12">
                  Use our Professional styled invoice layouts where you can add your own header graphics, or create your own using the Invoice Styler.
                </p>
                <button 
                  onClick={() => openLightbox('fraud-alerts')}
                  className="absolute bottom-4 right-4 px-4 py-1.5 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow cursor-pointer whitespace-nowrap"
                  style={{ backgroundColor: '#E0E7FF', color: '#7CB4FF' }}
                >
                  Learn
                </button>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow relative">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-customer-service-2-line text-red-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Committed Support</h3>
                <p className="text-gray-600 mb-12">
                  Our dedicated team is available to help you design a stylish invoice you can be proud of, and assist you and your clients with any XeroVerifier verification questions.
                </p>
                <button 
                  onClick={() => openLightbox('support')}
                  className="absolute bottom-4 right-4 px-4 py-1.5 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow cursor-pointer whitespace-nowrap"
                  style={{ backgroundColor: '#E0E7FF', color: '#7CB4FF' }}
                >
                  Learn
                </button>
              </div>
            </div>
          </div>

          {/* Analytics Dashboard Card - Hidden temporarily */}
          <div className="hidden">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow relative">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-bar-chart-line text-indigo-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Analytics Dashboard</h3>
              <p className="text-gray-600 mb-12">
                Track verification attempts, monitor security threats, and gain insights into your invoice security performance.
              </p>
              <button 
                onClick={() => openLightbox('analytics')}
                className="absolute bottom-4 right-4 px-4 py-1.5 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: '#E0E7FF', color: '#7CB4FF' }}
              >
                Learn
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white" style={{ backgroundColor: '#29B3E6' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Protect Your Business?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join thousands of businesses already using XeroVerifier to secure their invoices and protect their reputation. 
            Install our extension today and start verifying invoices in minutes.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="bg-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg whitespace-nowrap" 
              style={{ color: '#29B3E6' }} 
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F5F5'} 
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              onClick={openExtensionModal}
            >
              <i className="ri-chrome-line mr-2"></i>
              Install Business Xero Extension
            </button>
            <button 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors whitespace-nowrap"
              onClick={openConsumerModal}
            >
              <i className="ri-external-link-line mr-2"></i>
              Install Consumer Browser Extension
            </button>
          </div>
          
          <div className="flex items-center justify-center gap-8 mt-12 text-blue-100">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-xl"></i>
              <span>Enterprise Grade Security</span>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeLightbox && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4"
          onClick={closeLightbox}
        >
          <div 
            className="bg-white rounded-2xl overflow-hidden shadow-2xl"
            style={{ width: '650px', height: '630px', backgroundColor: '#F9FAFB', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="flex items-center justify-center px-6 relative"
              style={{ backgroundColor: '#7E67FF', height: '110px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
            >
              <h2 className="text-3xl font-bold text-white text-center">
                {featureDetails.find(f => f.id === activeLightbox)?.title}
              </h2>
              <button 
                onClick={closeLightbox}
                className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white transition-colors cursor-pointer w-8 h-8 flex items-center justify-center rounded"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-5" style={{ paddingLeft: '20px', paddingRight: '20px', paddingBottom: '20px', paddingTop: '45px' }}>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {featureDetails.find(f => f.id === activeLightbox)?.content}
              </p>
              {activeLightbox === 'qr-code' && (
                <div className="mt-6 flex justify-center">
                  <img 
                    src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/0834574f24c96bc913d22bba1e4a82fb.png"
                    alt="QR Code verification example"
                    className="rounded-lg shadow-lg"
                    style={{ maxWidth: '100%', height: 'auto', imageRendering: 'pixelated' }}
                  />
                </div>
              )}
              {activeLightbox === 'real-time' && (
                <div className="mt-6">
                  <img 
                    src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/c22ee2435f38c8299983e5e86aae0ce2.png"
                    alt="Real-time verification interface"
                    className="rounded-lg shadow-lg w-full"
                  />
                </div>
              )}
              {activeLightbox === 'tamper-proof' && (
                <div className="mt-6">
                  <img 
                    src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/d85d227d691cc35a1005f8e6da8eb941.png"
                    alt="Tamper-proof stamps technology"
                    className="rounded-lg shadow-lg mx-auto"
                    style={{ maxWidth: '100%', height: 'auto', imageRendering: 'pixelated', marginTop: '150px' }}
                  />
                </div>
              )}
              {activeLightbox === 'fraud-alerts' && (
                <div className="mt-6">
                  <div 
                    className="rounded-xl shadow-2xl w-full overflow-y-auto relative"
                    style={{ 
                      backgroundColor: '#F1F1EF',
                      height: '300px',
                      width: '100%'
                    }}
                  >
                    <img 
                      src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/8b792743fab37a62962de513d3840603.jpeg"
                      alt="Professionally styled invoice example"
                      className="object-center"
                      style={{ 
                        display: 'block', 
                        margin: '0 auto',
                        imageRendering: 'crisp-edges'
                      }}
                    />
                  </div>
                </div>
              )}
              {activeLightbox === 'support' && (
                <div className="mt-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Professional%20customer%20support%20team%20in%20modern%20office%20environment%2C%20diverse%20group%20of%20caucasian%20males%20and%20females%20in%20their%20late%20twenties%20to%20mid%20thirties%2C%20casually%20dressed%20in%20business%20casual%20attire%2C%20working%20at%20help%20desk%20with%20headsets%20and%20computers%2C%20friendly%20and%20approachable%20atmosphere%2C%20soft%20natural%20lighting%2C%20modern%20workspace%20with%20clean%20design%2C%20team%20collaboration%20and%20customer%20service%20focus&width=580&height=300&seq=support-team-desk&orientation=landscape"
                    alt="Dedicated support team ready to help"
                    className="rounded-lg w-full opacity-60 blur-sm"
                    style={{ filter: 'blur(1px) opacity(0.6)' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Extension Selection Modal */}
      <ExtensionSelectionModal 
        isOpen={isExtensionModalOpen}
        onClose={closeExtensionModal}
      />

      {/* Consumer Extension Modal */}
      <ConsumerExtensionModal 
        isOpen={isConsumerModalOpen}
        onClose={closeConsumerModal}
      />

      {/* Video Modal */}
      <VideoModal 
        isOpen={isVideoModalOpen}
        onClose={closeVideoModal}
        videoUrl="https://www.youtube.com/watch?v=8zmIAvugM2g&pp=ygUHYmlvbWFpbA%3D%3D"
        title="XeroVerifier Demo"
      />

      <Footer />
      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
}

